import { DEFAULT_CATCHUP_PLAYBACK_RATE, DEFAULT_SEEK_THRESHOLD_MULTIPLIER, DEFAULT_FALLBACK_PLAYBACK_RATE } from './constants.js';

const prefixes = [
  "webkit",
  "moz",
  "ms"
];
function getEventProperty(eventName) {
  return `on${eventName}`;
}
function prefixEventNameInDocument(standardEventName) {
  if (getEventProperty(standardEventName) in document) {
    return standardEventName;
  }
  return prefixes.find((prefix) => {
    const prefixedEventName = `${prefix}${standardEventName}`;
    return getEventProperty(prefixedEventName) in document;
  });
}
function getFullscreenChangeEventName() {
  const eventName = prefixEventNameInDocument("fullscreenchange");
  if (eventName && eventName.indexOf("ms") === 0) {
    return "MSFullscreenChange";
  }
  return eventName;
}
function getPrefixedPropertyIn(element, standardProperty) {
  let foundProperty = standardProperty;
  if (!(standardProperty in element)) {
    const capitalizedProperty = `${standardProperty.slice(0, 1).toUpperCase()}${standardProperty.slice(1)}`;
    foundProperty = prefixes.find((prefix) => `${prefix}${capitalizedProperty}` in element);
  }
  if (!foundProperty)
    return void 0;
  let property = element[foundProperty];
  if (typeof property === "function") {
    property = property.bind(element);
  }
  return property;
}
function getPrefixedPropertyInDocument(standardProperty) {
  return getPrefixedPropertyIn(document, standardProperty);
}
function addFullscreenChangeEventListener(listener) {
  const fullscreenChangeEventName = getFullscreenChangeEventName();
  if (!fullscreenChangeEventName)
    return;
  document.addEventListener(fullscreenChangeEventName, listener);
}
function removeFullscreenChangeEventListener(listener) {
  const fullscreenChangeEventName = getFullscreenChangeEventName();
  if (!fullscreenChangeEventName)
    return;
  document.removeEventListener(fullscreenChangeEventName, listener);
}
function exitFullscreen() {
  const prefixedExitFullscreen = getPrefixedPropertyInDocument("exitFullscreen");
  if (prefixedExitFullscreen) {
    return prefixedExitFullscreen();
  }
  if ("mozCancelFullScreen" in document) {
    return document.mozCancelFullScreen();
  }
  return Promise.reject(new Error("A function to exitFullscreen can not be found"));
}
function requestFullscreen(element, options) {
  let prefixedRequestFullscreen = getPrefixedPropertyIn(element, "requestFullscreen");
  if (!prefixedRequestFullscreen && "mozRequestFullScreen" in element) {
    prefixedRequestFullscreen = element.mozRequestFullScreen.bind(element);
  }
  if (!prefixedRequestFullscreen)
    return Promise.reject(new Error("Fullscreen API is not supported"));
  return prefixedRequestFullscreen(options);
}
function getFullscreenElement() {
  const fullscreenElement = getPrefixedPropertyInDocument("fullscreenElement");
  if (fullscreenElement === void 0 && "mozFullScreenElement" in document) {
    return document.mozFullScreenElement;
  }
  return fullscreenElement;
}
function getDefaultLowLatencyConfig(lowLatencyConfig) {
  const {
    targetLatency,
    catchup: {
      playbackRate: catchupPlaybackRate = DEFAULT_CATCHUP_PLAYBACK_RATE,
      playbackRateThreshold: catchupPlaybackRateThreshold = 0,
      seekThreshold: catchupSeekThreshold = targetLatency * DEFAULT_SEEK_THRESHOLD_MULTIPLIER
    } = {},
    fallback: {
      playbackRate: fallbackPlaybackRate = DEFAULT_FALLBACK_PLAYBACK_RATE,
      playbackRateThreshold: fallbackPlaybackRateThreshold = 0,
      seekThreshold: fallbackSeekThreshold = void 0
    } = {}
  } = lowLatencyConfig;
  return {
    targetLatency,
    catchup: {
      playbackRate: catchupPlaybackRate,
      playbackRateThreshold: catchupPlaybackRateThreshold,
      seekThreshold: catchupSeekThreshold
    },
    fallback: {
      playbackRate: fallbackPlaybackRate,
      playbackRateThreshold: fallbackPlaybackRateThreshold,
      seekThreshold: fallbackSeekThreshold
    }
  };
}
function mergePlayerRequests(...sources) {
  const result = {};
  for (const request of sources) {
    result.uris = request?.uris ?? result.uris;
    result.method = request?.method ?? result.method;
    result.headers = {
      ...result?.headers,
      ...request?.headers
    };
    result.body = request?.body ?? result.body;
    result.allowCrossSiteCredentials = request?.allowCrossSiteCredentials ?? result.allowCrossSiteCredentials;
  }
  return result;
}
function mergePlayerResponses(...sources) {
  const result = {};
  for (const response of sources) {
    result.uri = response?.uri ?? result.uri;
    result.originalUri = response?.originalUri ?? result.originalUri;
    result.status = response?.status ?? result.status;
    result.headers = {
      ...result?.headers,
      ...response?.headers
    };
    result.data = response?.data ?? result.data;
  }
  return result;
}
const createInterceptorComposer = (mergeFn) => (...functions) => {
  const reversed = functions.reverse();
  const composed = async (type, request) => {
    let previous = request;
    for (const fn of reversed) {
      const result = await fn?.(type, previous) || void 0;
      previous = mergeFn(previous, result);
    }
    return previous;
  };
  return composed;
};
const composePlayerRequestInterceptors = createInterceptorComposer(mergePlayerRequests);
const composePlayerResponseInterceptors = createInterceptorComposer(mergePlayerResponses);

export { addFullscreenChangeEventListener, composePlayerRequestInterceptors, composePlayerResponseInterceptors, exitFullscreen, getDefaultLowLatencyConfig, getFullscreenElement, getPrefixedPropertyIn, getPrefixedPropertyInDocument, mergePlayerRequests, mergePlayerResponses, removeFullscreenChangeEventListener, requestFullscreen };
//# sourceMappingURL=utils.js.map
